/**
 * Toolbar component for AtencionDialog
 */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { DialogTitle } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Save, Mail, Printer, CheckCircle, X, Send, ChevronDown, MessageCircle, Loader2 } from 'lucide-react';

const AtencionToolbar = ({
  codigo,
  readOnly,
  loading,
  canSendEmail,
  canPrint,
  canTerminar,
  allSolicitudesTerminado,
  actaAtencionUrl,
  actaEstado,
  whatsappEnabled,
  sendingEmail,
  sendingWhatsapp,
  onSave,
  onSendEmail,
  onSendWhatsApp,
  onPrint,
  onTerminar,
  onClose,
  sealLoading
}) => {
  const [showSendMenu, setShowSendMenu] = useState(false);
  const menuRef = useRef(null);
  const isSending = sendingEmail || sendingWhatsapp;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowSendMenu(false);
      }
    };
    if (showSendMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSendMenu]);

  return (
    <TooltipProvider>
      <div className="sticky top-0 z-10 bg-white border-b border-[#E8E8E7] px-4 py-3 flex items-center justify-between">
        <DialogTitle 
          className="text-lg sm:text-xl font-bold text-[#2D2D2D]" 
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          {readOnly ? 'Detalle de Atención' : 'Realizar Atención'} - {codigo}
        </DialogTitle>
        
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              {/* Grabar Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    onClick={onSave}
                    disabled={loading}
                    className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                    data-testid="atencion-save-btn"
                  >
                    <Save size={16} className="mr-1" />
                    <span className="hidden sm:inline">Grabar</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Guardar todos los cambios del formulario</p>
                </TooltipContent>
              </Tooltip>

              {/* Enviar Button - Dropdown when WhatsApp enabled */}
              {whatsappEnabled ? (
                <div className="relative" ref={menuRef}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canSendEmail || isSending || loading}
                        onClick={() => setShowSendMenu(!showSendMenu)}
                        className="border-blue-500 text-blue-500 disabled:opacity-50 disabled:border-gray-300 disabled:text-gray-400"
                        data-testid="atencion-send-dropdown-btn"
                      >
                        {isSending ? (
                          <Loader2 size={16} className="animate-spin mr-1" />
                        ) : (
                          <Send size={16} className="mr-1" />
                        )}
                        <span className="hidden sm:inline">Enviar</span>
                        <ChevronDown size={12} className="ml-0.5 sm:ml-1" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{canSendEmail 
                        ? 'Enviar acta al testigo' 
                        : 'Todas las solicitudes deben estar terminadas'}</p>
                    </TooltipContent>
                  </Tooltip>

                  {showSendMenu && (
                    <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px] overflow-hidden">
                      <button
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 text-left transition-colors"
                        onClick={() => { setShowSendMenu(false); onSendEmail(); }}
                        disabled={sendingEmail}
                        data-testid="atencion-send-by-email-option"
                      >
                        <Mail size={15} className="text-blue-600" />
                        <span>Correo</span>
                      </button>
                      <div className="border-t border-gray-100" />
                      <button
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 text-left transition-colors"
                        onClick={() => { setShowSendMenu(false); onSendWhatsApp(); }}
                        disabled={sendingWhatsapp}
                        data-testid="atencion-send-by-whatsapp-option"
                      >
                        <MessageCircle size={15} className="text-green-600" />
                        <span>WhatsApp</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onSendEmail}
                      disabled={!canSendEmail || loading}
                      className="border-blue-500 text-blue-500 disabled:opacity-50 disabled:border-gray-300 disabled:text-gray-400"
                      data-testid="atencion-email-btn"
                    >
                      <Mail size={16} className="mr-1" />
                      <span className="hidden sm:inline">Enviar</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{canSendEmail 
                      ? 'Enviar informe por correo al testigo' 
                      : 'Todas las solicitudes deben estar terminadas'}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </>
          )}

          {/* Imprimir Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={onPrint}
                disabled={!canPrint || sealLoading}
                className="border-gray-500 text-gray-500 disabled:opacity-50 disabled:border-gray-300 disabled:text-gray-400"
                data-testid="atencion-print-btn"
              >
                {sealLoading ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Printer size={16} className="mr-1" />}
                <span className="hidden sm:inline">{sealLoading ? 'Cargando...' : 'Imprimir'}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{sealLoading 
                ? 'Cargando sello digital...'
                : canPrint 
                ? 'Generar PDF con espacio para firmas' 
                : 'Todas las solicitudes deben estar terminadas'}</p>
            </TooltipContent>
          </Tooltip>

          {!readOnly && (
            /* Terminar Atención Button */
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={onTerminar}
                  disabled={!canTerminar || loading}
                  className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:bg-gray-300"
                  data-testid="atencion-terminar-btn"
                >
                  <CheckCircle size={16} className="mr-1" />
                  <span className="hidden sm:inline">Terminar</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{!allSolicitudesTerminado 
                  ? 'Todas las solicitudes deben estar terminadas' 
                  : !actaAtencionUrl && !['aprobado_correo', 'aprobado_whatsapp'].includes(actaEstado)
                    ? 'El acta de atención debe estar adjuntada o aprobada'
                    : 'Finalizar la atención y marcar requerimiento como Terminado'}</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          <div className="hidden sm:block w-px h-6 bg-gray-300 mx-1" />
          
          {/* Cerrar Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                data-testid="atencion-close-btn"
              >
                <X size={18} />
                <span className="hidden sm:inline ml-1">Cerrar</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Cerrar formulario</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AtencionToolbar;
